import React, { useState, useEffect, useRef } from 'react';
import { Target, TrendingUp, Shield, Plus, AlertCircle, CheckCircle, Lightbulb, RefreshCw, Sparkles, LayoutGrid } from 'lucide-react';
import { supabase } from '../../../../../lib/supabase';
import toast from 'react-hot-toast';
import TierAnalysisLoader from '../../components/TierAnalysisLoader';
import TierDefinitionCards from './components/TierDefinitionCards';
import CompletionRoadmap from './components/CompletionRoadmap';
import StrategicTipsCarousel from './components/StrategicTipsCarousel';
import OverviewTab from './components/OverviewTab';
import TierComparisonView from './components/TierComparisonView';
import AITierSummary from './components/AITierSummary';
import AlternativeSchools from './components/AlternativeSchools';
import { getStudentTier, getTierDefinitions, validatePortfolio } from '../../utils/tierFramework';
import { calculateSchoolFitScores, extractCollegeInsights } from '../../utils/schoolFitScore';

/**
 * Transform Gap Analysis API response to match UI expectations
 * API returns: { dimensions: { selectivity: {...}, geographic_diversity: {...}, ... }, priority_actions: [] }
 * UI expects: { missingSchoolTypes: [], geographicGaps: [], programGaps: [], recommendations: [], overallAssessment: "" }
 */
const transformGapAnalysis = (apiResponse) => {
  if (!apiResponse?.dimensions) {
    // If old format or invalid, return as-is
    return apiResponse;
  }

  const { dimensions, priority_actions } = apiResponse;

  const transformed = {
    missingSchoolTypes: [],
    geographicGaps: [],
    programGaps: [],
    recommendations: [],
    overallAssessment: ""
  };

  // Map dimensions to UI categories
  const problemDimensions = Object.entries(dimensions).filter(([_, dim]) => dim.status === '✗');

  // Categorize issues (without prepending dimension keys - issues are already descriptive)
  problemDimensions.forEach(([key, dimension]) => {
    const { issue, recommendation } = dimension;

    if (key === 'selectivity' || key === 'institutional_type' || key === 'strategic_positioning' || key === 'financial_profile' || key === 'cultural_fit' || key === 'career_outcomes') {
      if (issue) transformed.missingSchoolTypes.push(issue);
      if (recommendation) transformed.recommendations.push(recommendation);
    } else if (key === 'geographic_diversity') {
      if (issue) transformed.geographicGaps.push(issue);
      if (recommendation) transformed.recommendations.push(recommendation);
    } else if (key === 'program_availability') {
      if (issue) transformed.programGaps.push(issue);
      if (recommendation) transformed.recommendations.push(recommendation);
    }
  });

  // Add priority actions to recommendations
  if (priority_actions && priority_actions.length > 0) {
    transformed.recommendations = [...new Set([...transformed.recommendations, ...priority_actions])];
  }

  // Create overall assessment
  const goodCount = Object.values(dimensions).filter(d => d.status === '✓').length;
  const totalCount = Object.keys(dimensions).length;

  if (goodCount === totalCount) {
    transformed.overallAssessment = "Your portfolio is well-balanced across all dimensions!";
  } else if (problemDimensions.length <= 2) {
    transformed.overallAssessment = `Your portfolio is strong overall with ${problemDimensions.length} area${problemDimensions.length > 1 ? 's' : ''} to address.`;
  } else {
    transformed.overallAssessment = `Your portfolio needs attention in ${problemDimensions.length} key areas for better balance and admission probability.`;
  }

  return transformed;
};

const TierAnalysis = ({ colleges = [], currentUser, portfolioStatus = {}, studentProfile = {}, currentView = 'tier', onViewChange, onDeleteCollege }) => {
  // Portfolio configuration - TODO: Make user-configurable via settings
  const TIER_ANALYSIS_MINIMUM = 4; // Default 4/4/4 portfolio target

  const [activeTab, setActiveTab] = useState('overview');
  const [showAlternatives, setShowAlternatives] = useState(false);
  const tabContentRef = useRef(null);
  const navRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);

  // Track previous college composition to detect changes
  const [previousColleges, setPreviousColleges] = useState({ reach: 0, target: 0, safety: 0 });

  // Handle tab change with scroll to tab content
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    // Scroll to the tab content section with offset for sticky nav
    if (tabContentRef.current) {
      const element = tabContentRef.current;
      const offsetTop = element.offsetTop - 200; // Account for sticky nav height + some padding
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  // Detect when nav becomes sticky
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(!entry.isIntersecting);
      },
      { threshold: [1], rootMargin: '-128px 0px 0px 0px' } // top-32 = 128px
    );

    if (navRef.current) {
      observer.observe(navRef.current);
    }

    return () => {
      if (navRef.current) {
        observer.unobserve(navRef.current);
      }
    };
  }, []);

  // AI Analysis State
  const [tierAnalysis, setTierAnalysis] = useState({
    reach: null,
    target: null,
    safety: null
  });
  const [tierAlternatives, setTierAlternatives] = useState({
    reach: null,
    target: null,
    safety: null
  });
  const [tierSchoolInsights, setTierSchoolInsights] = useState({
    reach: null,
    target: null,
    safety: null
  });
  const [whatsMissing, setWhatsMissing] = useState(null);
  const [loadingTier, setLoadingTier] = useState({ reach: false, target: false, safety: false });
  const [loadingAlternatives, setLoadingAlternatives] = useState({ reach: false, target: false, safety: false });
  const [loadingSchoolInsights, setLoadingSchoolInsights] = useState({ reach: false, target: false, safety: false });
  const [loadingMissing, setLoadingMissing] = useState(false);
  const [loadingCache, setLoadingCache] = useState(true); // Track cache loading to prevent premature auto-analysis
  const [autoAnalyzedTiers, setAutoAnalyzedTiers] = useState({ reach: false, target: false, safety: false }); // Track which tiers have been auto-analyzed
  const [portfolioValidation, setPortfolioValidation] = useState({ warnings: [], successes: [] }); // Portfolio validation results

  // Group colleges by tier
  const collegesByTier = {
    reach: colleges.filter(c => c.category?.toLowerCase() === 'reach'),
    target: colleges.filter(c => c.category?.toLowerCase() === 'target'),
    safety: colleges.filter(c => c.category?.toLowerCase() === 'safety')
  };

  // Run portfolio validation when colleges change
  useEffect(() => {
    if (colleges.length >= 3) {
      const validation = validatePortfolio(colleges, studentProfile);
      setPortfolioValidation(validation);
    } else {
      setPortfolioValidation({ warnings: [], successes: [] });
    }
  }, [colleges, studentProfile]);

  // Load cached analysis on mount
  useEffect(() => {
    const fetchCachedAnalysis = async () => {
      if (!currentUser?.id) {
        console.log('⚠️ No currentUser.id, skipping cache load');
        setLoadingCache(false);
        return;
      }

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser.id;

      console.log('📥 [TierAnalysis] Loading cached analysis for user:', {
        currentUserId: currentUser.id,
        selectedStudentId: selectedStudentUserId,
        queryingWith: userIdForQuery
      });

      try {
        const { data, error } = await supabase
          .from('portfolio_analysis')
          .select('tier_confidence, portfolio_balance')
          .eq('user_id', userIdForQuery)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('❌ Error fetching cached analysis:', error);
          setLoadingCache(false);
          return;
        }

        console.log('📊 Cache query result:', {
          hasData: !!data,
          hasTierConfidence: !!data?.tier_confidence,
          tierKeys: data?.tier_confidence ? Object.keys(data.tier_confidence) : []
        });

        if (data?.tier_confidence) {
          console.log('✅ Loaded cached tier analysis:', Object.keys(data.tier_confidence).filter(k => data.tier_confidence[k]));
          setTierAnalysis(data.tier_confidence);

          // Load cached alternatives from tier_confidence
          const alternatives = {};
          ['reach', 'target', 'safety'].forEach(tier => {
            if (data.tier_confidence[tier]?.alternatives) {
              alternatives[tier] = data.tier_confidence[tier].alternatives;
            }
          });
          if (Object.keys(alternatives).length > 0) {
            setTierAlternatives(alternatives);
          }
        } else {
          console.log('ℹ️ No cached tier analysis found');
        }

        if (data?.portfolio_balance?.whats_missing) {
          setWhatsMissing(data.portfolio_balance.whats_missing);
        }

        // Load cached school insights from colleges
        if (colleges.length > 0) {
          console.log('🔍 [TierAnalysis] Checking for cached insights in', colleges.length, 'colleges');

          const schoolInsights = { reach: {}, target: {}, safety: {} };

          colleges.forEach(college => {
            const collegeName = college.global_colleges?.name || college.name;
            const tier = college.category?.toLowerCase();

            console.log(`   Checking "${collegeName}":`, {
              tier,
              hasCompetitivePosition: !!college.competitive_position,
              competitivePositionKeys: college.competitive_position ? Object.keys(college.competitive_position) : [],
              hasTierInsights: !!(college.competitive_position?.tier_insights),
              tierInsightsData: college.competitive_position?.tier_insights
            });

            if (tier && (tier === 'reach' || tier === 'target' || tier === 'safety')) {
              const cachedInsights = college.competitive_position?.tier_insights;
              if (cachedInsights && cachedInsights.tier === tier) {
                schoolInsights[tier][collegeName] = {
                  mayNotKnow: cachedInsights.mayNotKnow || [],
                  betterThanYouThink: cachedInsights.betterThanYouThink || '',
                  hiddenAdvantage: cachedInsights.hiddenAdvantage || ''
                };
                console.log(`   ✅ Loaded cached insights for "${collegeName}"`);
              } else if (cachedInsights) {
                console.log(`   ⚠️ Tier mismatch for "${collegeName}": insights tier = ${cachedInsights.tier}, college tier = ${tier}`);
              }
            }
          });

          // Only set if we found any cached insights
          if (Object.values(schoolInsights).some(tier => Object.keys(tier).length > 0)) {
            setTierSchoolInsights(schoolInsights);
            console.log('✅ Loaded cached school insights:', Object.entries(schoolInsights).filter(([_, v]) => Object.keys(v).length > 0).map(([k]) => k));
          } else {
            console.log('ℹ️ No cached school insights found in any college');
          }
        }
      } catch (error) {
        console.error('Error loading cached analysis:', error);
      } finally {
        setLoadingCache(false);
      }
    };

    fetchCachedAnalysis();
  }, [currentUser?.id, colleges]);

  // Auto-analyze tiers when conditions met (UX Philosophy: Maximally Assistive)
  useEffect(() => {
    if (loadingCache) {
      console.log('⏳ Waiting for cache to load before auto-analyzing...');
      return; // Wait for cache to load first
    }

    console.log('🔍 Checking if auto-analysis needed:', {
      loadingCache,
      tierAnalysis,
      autoAnalyzedTiers,
      collegeCount: colleges.length
    });

    ['reach', 'target', 'safety'].forEach(tier => {
      const tierColleges = collegesByTier[tier];
      const hasEnoughSchools = tierColleges.length >= TIER_ANALYSIS_MINIMUM;
      const hasAnalysis = tierAnalysis[tier];
      const alreadyAnalyzed = autoAnalyzedTiers[tier];

      console.log(`  [${tier}] Auto-analyze check:`, {
        hasEnoughSchools,
        hasAnalysis: !!hasAnalysis,
        alreadyAnalyzed,
        loadingTier: loadingTier[tier],
        shouldAutoAnalyze: hasEnoughSchools && !hasAnalysis && !alreadyAnalyzed && !loadingTier[tier]
      });

      // Auto-analyze if: 4+ schools, no cached analysis, hasn't been auto-analyzed yet
      if (hasEnoughSchools && !hasAnalysis && !alreadyAnalyzed && !loadingTier[tier]) {
        console.log(`🤖 Auto-analyzing ${tier} tier (${tierColleges.length} schools)`);
        analyzeTierBalance(tier);
        setAutoAnalyzedTiers(prev => ({ ...prev, [tier]: true }));
      } else if (hasAnalysis) {
        console.log(`✅ [${tier}] Using cached analysis, skipping auto-analyze`);
      }
    });
  }, [loadingCache, colleges.length, tierAnalysis]); // Add tierAnalysis as dependency

  // Detect tier composition changes and re-analyze (UX Philosophy: Contextually Intelligent)
  useEffect(() => {
    if (loadingCache) {
      console.log('⏳ Skipping composition check while cache is loading');
      return; // Don't check composition changes while cache is loading
    }

    const current = {
      reach: collegesByTier.reach.length,
      target: collegesByTier.target.length,
      safety: collegesByTier.safety.length
    };

    // Skip initial run (when previousColleges is 0,0,0)
    const isInitialRun = previousColleges.reach === 0 && previousColleges.target === 0 && previousColleges.safety === 0;
    if (isInitialRun && (current.reach > 0 || current.target > 0 || current.safety > 0)) {
      console.log('📋 Initial composition detected, setting baseline without analyzing:', current);
      setPreviousColleges(current);
      return;
    }

    ['reach', 'target', 'safety'].forEach(tier => {
      const changed = current[tier] !== previousColleges[tier];
      const hasEnough = current[tier] >= TIER_ANALYSIS_MINIMUM;
      const hadEnough = previousColleges[tier] >= TIER_ANALYSIS_MINIMUM;

      if (changed) {
        console.log(`🔄 ${tier} tier changed: ${previousColleges[tier]} → ${current[tier]}`);

        // If tier composition changed and still has 4+, re-analyze
        if (hasEnough && hadEnough && tierAnalysis[tier]) {
          console.log(`♻️ Re-analyzing ${tier} tier due to composition change`);
          analyzeTierBalance(tier);
        }

        // If tier now has 4+ schools but didn't before, auto-analyze
        if (hasEnough && !hadEnough && !loadingTier[tier]) {
          console.log(`✨ ${tier} tier now ready for analysis (${current[tier]} schools)`);
          analyzeTierBalance(tier);
        }

        // If tier drops below 4, clear analysis
        if (!hasEnough && hadEnough) {
          console.log(`⚠️ ${tier} tier below minimum, clearing analysis`);
          setTierAnalysis(prev => ({ ...prev, [tier]: null }));
        }
      }
    });

    setPreviousColleges(current);
  }, [colleges, loadingCache]); // Run whenever colleges array changes

  // Manual analyze all tiers function
  const analyzeAllTiers = async () => {
    const tiersToAnalyze = ['reach', 'target', 'safety'].filter(tier => {
      const tierColleges = collegesByTier[tier];
      const hasAnalysis = tierAnalysis[tier];
      return tierColleges.length >= TIER_ANALYSIS_MINIMUM && !hasAnalysis; // Analyze tiers with 4+ colleges that don't have analysis
    });

    if (tiersToAnalyze.length === 0) {
      toast.error('All tiers with 4+ colleges are already analyzed');
      return;
    }

    for (const tier of tiersToAnalyze) {
      await analyzeTierBalance(tier);
    }
  };

  // Analyze tier balance
  const analyzeTierBalance = async (tier) => {
    setLoadingTier(prev => ({ ...prev, [tier]: true }));

    try {
      const tierColleges = collegesByTier[tier];

      if (tierColleges.length === 0) {
        toast.error(`Add some ${tier} schools first`);
        return;
      }

      // Pre-compute student tier and definitions (Performance optimization + better AI prompts)
      const studentTierData = getStudentTier(studentProfile);
      const tierDefinitions = getTierDefinitions(studentTierData);

      console.log(`📊 Student Tier: ${studentTierData.label} (Tier ${studentTierData.id})`);
      console.log(`📋 ${tier.toUpperCase()} Definition:`, tierDefinitions[tier]);

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      console.log(`📋 Using user ID for tier analysis save:`, {
        currentUserId: currentUser?.id,
        selectedStudentId: selectedStudentUserId,
        finalUserId: userIdForQuery
      });

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'analyze-tier-balance',
          tier,
          colleges: tierColleges.map(c => {
            // Don't need ID for tier balance, but avoid spreading issues
            const { id: globalId, ...globalCollegeData } = c.global_colleges || {};
            return {
              ...globalCollegeData,
              name: c.global_colleges?.name || c.name,
              tier: c.category,
              location: `${c.global_colleges?.city}, ${c.global_colleges?.state}`
            };
          }),
          studentProfile: {
            ...studentProfile,
            computed_tier: studentTierData, // Add pre-computed tier
            tier_definitions: tierDefinitions // Add pre-filtered definitions
          },
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to analyze ${tier} tier`);
      }

      const { analysis } = await response.json();

      setTierAnalysis(prev => ({
        ...prev,
        [tier]: analysis
      }));

      toast.success(`${tier.charAt(0).toUpperCase() + tier.slice(1)} tier analysis complete`);

      // Automatically generate school insights after tier analysis completes
      await analyzeTierSchoolInsights(tier);
    } catch (error) {
      console.error(`Error analyzing ${tier} tier:`, error);

      // Don't retry automatically - user can manually click if needed
      const errorMessage = error.message || `Failed to analyze ${tier} tier`;

      // Check for specific error types
      if (errorMessage.includes('426') || errorMessage.includes('Upgrade Required')) {
        toast.error('API connection issue. Please refresh the page and try again.', { duration: 5000 });
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoadingTier(prev => ({ ...prev, [tier]: false }));
    }
  };

  // Analyze tier school insights (called after tier balance analysis)
  const analyzeTierSchoolInsights = async (tier) => {
    setLoadingSchoolInsights(prev => ({ ...prev, [tier]: true }));

    try {
      const tierColleges = collegesByTier[tier];

      if (tierColleges.length === 0) {
        console.log(`No ${tier} schools to analyze for insights`);
        return;
      }

      console.log(`💎 Generating school insights for ${tierColleges.length} ${tier} schools`);

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      console.log(`📋 Using user ID for insights save:`, {
        currentUserId: currentUser?.id,
        selectedStudentId: selectedStudentUserId,
        finalUserId: userIdForQuery
      });

      console.log(`📋 Colleges being sent to API:`, tierColleges.map(c => ({
        id: c.id,
        name: c.global_colleges?.name || c.name,
        category: c.category
      })));

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'analyze-tier-school-insights',
          tier,
          colleges: tierColleges.map(c => {
            // Spread global_colleges first, THEN override with correct IDs
            const { id: globalId, ...globalCollegeData } = c.global_colleges || {};
            return {
              ...globalCollegeData,
              id: c.id, // college_journey_colleges.id (not global_colleges.id!)
              name: c.global_colleges?.name || c.name,
              location: `${c.global_colleges?.city}, ${c.global_colleges?.state}`
            };
          }),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to generate ${tier} school insights`);
      }

      const { insights } = await response.json();

      console.log(`📦 [${tier}] Raw insights from API:`, insights);
      console.log(`📦 [${tier}] Insights keys:`, Object.keys(insights || {}));
      console.log(`📦 [${tier}] Sample insight:`, insights ? insights[Object.keys(insights)[0]] : 'none');

      setTierSchoolInsights(prev => ({
        ...prev,
        [tier]: insights
      }));

      console.log(`✅ Generated insights for ${Object.keys(insights).length} ${tier} schools`);
      console.log(`🔄 [${tier}] About to set loadingSchoolInsights to FALSE`);

      toast.success(`School insights generated for ${tier} tier`);
    } catch (error) {
      console.error(`Error generating ${tier} school insights:`, error);
      toast.error(error.message || `Failed to generate ${tier} school insights`);
    } finally {
      setLoadingSchoolInsights(prev => {
        const newState = { ...prev, [tier]: false };
        console.log(`✨ [${tier}] Loading state updated:`, newState);
        return newState;
      });
    }
  };

  // Analyze what's missing from portfolio
  const analyzeWhatsMissing = async () => {
    setLoadingMissing(true);

    try {
      if (colleges.length < 3) {
        toast.error('Add at least 3 colleges for gap analysis');
        return;
      }

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'generate-whats-missing',
          portfolio: colleges.map(c => {
            const { id: globalId, ...globalCollegeData } = c.global_colleges || {};
            return {
              ...globalCollegeData,
              name: c.global_colleges?.name || c.name,
              tier: c.category,
              location: `${c.global_colleges?.city}, ${c.global_colleges?.state}`
            };
          }),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to analyze portfolio gaps');
      }

      const { analysis } = await response.json();

      // Transform API response structure to match UI expectations
      const transformedAnalysis = transformGapAnalysis(analysis);
      setWhatsMissing(transformedAnalysis);
      toast.success('Portfolio gap analysis complete');
    } catch (error) {
      console.error('Error analyzing portfolio gaps:', error);
      toast.error(error.message || 'Failed to analyze portfolio gaps');
    } finally {
      setLoadingMissing(false);
    }
  };

  // Analyze tier alternatives
  const analyzeTierAlternatives = async (tier) => {
    setLoadingAlternatives(prev => ({ ...prev, [tier]: true }));

    try {
      const tierColleges = collegesByTier[tier];

      if (tierColleges.length < 2) {
        toast.error(`Add at least 2 ${tier} schools to get alternatives`);
        return;
      }

      // Check for selected student ID (for parent users)
      const selectedStudentUserId = localStorage.getItem('college-selected-student-id');
      const userIdForQuery = selectedStudentUserId || currentUser?.id;

      const response = await fetch('/api/anthropic/analysis', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'suggest-tier-alternatives',
          tier,
          currentColleges: tierColleges.map(c => {
            const { id: globalId, ...globalCollegeData } = c.global_colleges || {};
            return {
              ...globalCollegeData,
              name: c.global_colleges?.name || c.name,
              location: `${c.global_colleges?.city}, ${c.global_colleges?.state}`
            };
          }),
          studentProfile,
          userId: userIdForQuery
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to suggest ${tier} alternatives`);
      }

      const { alternatives } = await response.json();

      setTierAlternatives(prev => ({
        ...prev,
        [tier]: alternatives
      }));

      toast.success(`Alternative ${tier} schools suggested`);
    } catch (error) {
      console.error(`Error suggesting ${tier} alternatives:`, error);
      toast.error(error.message || `Failed to suggest ${tier} alternatives`);
    } finally {
      setLoadingAlternatives(prev => ({ ...prev, [tier]: false }));
    }
  };

  // Helper: Get tier-specific validation warnings
  const getTierValidationWarnings = (tier) => {
    return portfolioValidation.warnings.filter(w => {
      // Filter warnings by type - match tier-specific warnings
      if (tier === 'target' && w.type === 'diversity') return true;
      if (tier === 'safety' && w.type === 'safety') return true;
      if (w.type === 'sat_mismatch' && w.collegeName) {
        // Check if the college belongs to this tier
        const college = colleges.find(c =>
          (c.global_colleges?.name || c.name) === w.collegeName
        );
        return college?.category?.toLowerCase() === tier;
      }
      return false;
    });
  };

  // Helper: Get portfolio-wide warnings (not tier-specific)
  const getPortfolioWideWarnings = () => {
    return portfolioValidation.warnings.filter(w =>
      w.type === 'balance' || w.type === 'geographic'
    );
  };

  // Tab definitions - Updated to match mockup
  const tabs = [
    { id: 'overview', label: 'Overview', icon: LayoutGrid, count: null },
    { id: 'reach', label: `Reach Tier (${collegesByTier.reach.length})`, icon: TrendingUp, count: collegesByTier.reach.length },
    { id: 'target', label: `Target Tier (${collegesByTier.target.length})`, icon: Target, count: collegesByTier.target.length },
    { id: 'safety', label: `Safety Tier (${collegesByTier.safety.length})`, icon: Shield, count: collegesByTier.safety.length }
  ];

  const renderTierAnalysis = (tier) => {
    const tierColleges = collegesByTier[tier];
    const analysis = tierAnalysis[tier];
    const alternatives = tierAlternatives[tier];
    const schoolInsights = tierSchoolInsights[tier];

    // Calculate fit scores and extract insights for each college
    const collegesWithEnrichment = tierColleges.map(college => ({
      ...college,
      fitScores: calculateSchoolFitScores(college, studentProfile),
      insights: extractCollegeInsights(college, studentProfile)
    }));

    const handleRegenerateTier = async () => {
      // Re-run both the tier analysis and school insights
      await analyzeTierBalance(tier);
      // analyzeTierSchoolInsights is called automatically after analyzeTierBalance completes
    };

    return (
      <div className="space-y-6">
        {/* Loading Banner - Prominent at top */}
        {loadingTier[tier] && (
          <div className="bg-teal-50 border-2 border-teal-500 rounded-xl p-6 mb-6">
            <TierAnalysisLoader tier={tier} />
          </div>
        )}

        {/* Regenerate Button */}
        {tierColleges.length >= TIER_ANALYSIS_MINIMUM && (
          <div className="flex justify-end">
            <button
              onClick={handleRegenerateTier}
              disabled={loadingTier[tier] || loadingSchoolInsights[tier]}
              className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${(loadingTier[tier] || loadingSchoolInsights[tier]) ? 'animate-spin' : ''}`} />
              {loadingTier[tier] || loadingSchoolInsights[tier] ? 'Regenerating...' : 'Regenerate Analysis'}
            </button>
          </div>
        )}

        {/* Tier Comparison View - New horizontal scrolling layout */}
        <TierComparisonView
          tier={tier}
          colleges={collegesWithEnrichment}
          studentProfile={studentProfile}
          analysis={analysis}
          schoolInsights={schoolInsights}
          loadingInsights={loadingSchoolInsights[tier]}
          onDeleteCollege={onDeleteCollege}
        />

        {/* AI Analysis - Intelligent Status Display */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-slate-900 mb-3">
            AI-Powered Tier Analysis
          </h3>

          {tierColleges.length < TIER_ANALYSIS_MINIMUM ? (
            // Guidance when insufficient schools (UX Philosophy: Strategically Transparent)
            <div className="bg-slate-50 border-2 border-slate-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-slate-700 mb-1">
                    Add {TIER_ANALYSIS_MINIMUM - tierColleges.length} more {tier} {TIER_ANALYSIS_MINIMUM - tierColleges.length === 1 ? 'school' : 'schools'} to unlock AI tier analysis
                  </p>
                  <p className="text-xs text-slate-600">
                    Your target: {TIER_ANALYSIS_MINIMUM} {tier} schools. With {TIER_ANALYSIS_MINIMUM}+ schools, we can analyze balance, identify gaps, and provide strategic recommendations.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* AI Tier Summary - New component */}
        {analysis && !loadingTier[tier] && (
          <AITierSummary tier={tier} analysis={analysis} colleges={tierColleges} />
        )}

        {/* Alternative Schools - Manual, optional feature */}
        {analysis && !loadingTier[tier] && tierColleges.length >= 2 && (
          <div className="mt-6">
            {alternatives && alternatives.length > 0 ? (
              <AlternativeSchools tier={tier} alternatives={alternatives} />
            ) : (
              <button
                onClick={() => analyzeTierAlternatives(tier)}
                disabled={loadingAlternatives[tier]}
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingAlternatives[tier] ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Finding alternatives...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4" />
                    Explore Alternative Schools
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };


  return (
    <div>
      {/* Add CSS for horizontal scrolling and school cards */}
      <style>{`
        .school-card {
          min-width: 340px;
          max-width: 340px;
        }
        .horizontal-scroll {
          overflow-x: auto;
          scrollbar-width: thin;
          scrollbar-color: #cbd5e1 #f1f5f9;
        }
        .horizontal-scroll::-webkit-scrollbar {
          height: 8px;
        }
        .horizontal-scroll::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .horizontal-scroll::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
      `}</style>

      {/* Sentinel for sticky detection */}
      <div ref={navRef} className="h-0" />

      {/* Tab Navigation - Sticky */}
      <div className={`sticky top-32 z-50 -mx-8 px-8 py-4 transition-colors duration-200 ${
        isSticky ? 'bg-slate-900' : ''
      }`}>
        <div className="bg-white border border-slate-200 rounded-xl p-1 flex gap-1 overflow-x-auto">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            // All tabs are always available now
            const isAvailable = tab.id === 'overview' || tab.count !== null;

            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 border-0 text-sm font-medium cursor-pointer rounded-lg transition-all duration-200 whitespace-nowrap flex-shrink-0 flex items-center gap-2 ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'bg-transparent text-slate-500 hover:text-slate-800 hover:bg-slate-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div ref={tabContentRef} className="mt-4">
        {activeTab === 'overview' && (
          <OverviewTab
            collegesByTier={collegesByTier}
            onTabChange={setActiveTab}
            studentProfile={studentProfile}
          />
        )}
        {activeTab === 'reach' && renderTierAnalysis('reach')}
        {activeTab === 'target' && renderTierAnalysis('target')}
        {activeTab === 'safety' && renderTierAnalysis('safety')}
      </div>
    </div>
  );
};

export default TierAnalysis;